import { DocumentationLegacyGroup, } from "../../model/documentation/SDKDocumentationLegacyGroup";
import { DocumentationLegacyPage, } from "../../model/documentation/SDKDocumentationLegacyPage";
import { DocumentationLegacyItemType } from "../../model/enums/SDKDocumentationLegacyItemType";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Function Definition
export class DocumentationLegacyItemResolver {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(customBlocks, configuration, designSystemId, designSystemName, versionId, workspaceHandle, docsUrl) {
        // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
        // MARK: - Properties
        this.hashedItems = new Map();
        this.resolvedItems = new Map();
        this.customBlocks = customBlocks;
        this.configuration = configuration;
        this.versionId = versionId;
        this.designSystemId = designSystemId;
        this.designSystemName = designSystemName;
        this.workspaceHandle = workspaceHandle;
        this.docsUrl = docsUrl;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Resolution
    resolveItemData(pageDetails, groupDetails) {
        // Hash groups for faster lookup + find root group. Also fix the missing type attribute
        let rootGroupModel;
        for (const item of groupDetails) {
            item.type = DocumentationLegacyItemType.group;
            this.hashedItems.set(item.persistentId, item);
            if (item.isRoot) {
                rootGroupModel = item;
            }
        }
        // @ts-expect-error TS(2454): Variable 'rootGroupModel' is used before being ass... Remove this comment to see the full error message
        if (!rootGroupModel) {
            throw new Error(`Incosistent model detected, missing root group`);
        }
        // Hash pages for faster lookup. Also fix the missing type attribute
        for (const page of pageDetails) {
            page.type = DocumentationLegacyItemType.page;
            this.hashedItems.set(page.persistentId, page);
        }
        // Create structured chain of objects
        const rootGroup = new DocumentationLegacyGroup(rootGroupModel);
        this.resolveItemsForGroup(rootGroup);
        // Retrieve created objects
        const items = Array.from(this.resolvedItems.values());
        // Compute different paths of objects
        for (const item of items) {
            if (item instanceof DocumentationLegacyPage) {
                const deployed = this.deployedUrl(item);
                const editor = this.editorUrl(item);
                // const relative: string | null = this.pageUrl(item)
                item.internalOverridePaths(deployed, editor);
            }
            else if (item instanceof DocumentationLegacyGroup) {
                const relative = this.pageUrl(item);
                let deployed = null;
                const page = this.firstPageFromTop(item);
                if (page) {
                    deployed = this.deployedUrl(page);
                }
                item.internalOverridePaths(relative, deployed);
            }
        }
        return items;
    }
    resolveItemsForGroup(group) {
        // Store items
        this.resolvedItems.set(group.id, group);
        // Resolve children of one group
        for (const childId of group.childrenIds) {
            // Find model from the loaded items
            const childModel = this.hashedItems.get(childId);
            if (!childModel) {
                throw new Error(`Inconsistent group / page model detected in one of the documentation items, missing ${childId}`);
            }
            if (childModel.type === DocumentationLegacyItemType.group) {
                // If it is a group, then create a group
                const subgroup = new DocumentationLegacyGroup(childModel);
                subgroup.parent = group;
                group.addChild(subgroup);
                this.resolveItemsForGroup(subgroup);
            }
            else {
                // Otherwise, create a page using the data we obtained from the content call
                const pageId = childModel.persistentId;
                const pageModel = this.hashedItems.get(pageId);
                const page = new DocumentationLegacyPage(pageModel, this.customBlocks, this.configuration);
                // Resolve block chains
                this.resolveBlockChains(page);
                // Link parents properly
                page.parent = group;
                page.type = DocumentationLegacyItemType.page;
                group.addChild(page);
                // Store items
                this.resolvedItems.set(page.id, page);
            }
        }
    }
    resolveBlockChains(page) {
        // Resolve initial block group
        this.resolveBlockGroup(page.blocks);
    }
    resolveBlockGroup(blocks) {
        // Ignore empty groups
        if (!blocks || blocks.length === 0) {
            return;
        }
        if (blocks.length === 1 && blocks[0] === undefined) {
            // Note in some really rare cases, blocks[0] can be undefined. We should not be resolving undefined groups, rather ignoring it. This bug points to issue somewhere else however.
            throw new Error("The fuck?");
        }
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Documentati... Remove this comment to see the full error message
        let blockType = null;
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Documentati... Remove this comment to see the full error message
        let previousBlock = null;
        for (const block of blocks) {
            // For previous block, check if it ended
            if (previousBlock) {
                previousBlock.endsTypeChain = previousBlock.type !== block.type;
            }
            // If previous block is of different type (or initial), it beings type chain, otherwise it follows previous one
            block.beginsTypeChain = block.type !== blockType;
            blockType = block.type;
            previousBlock = block;
            // Also solve for all subblocks who can form independent chains
            this.resolveBlockGroup(block.children);
        }
        // Final block always ends chain
        previousBlock.endsTypeChain = true;
    }
    /** Create URL pointing to the deployed documentation page hosted on Supernova side. Will be empty if there was nothing deployed, or if not hosted at Supernova */
    deployedUrl(page) {
        if (this.docsUrl) {
            const { relativeUrl } = page;
            if (relativeUrl) {
                return `${this.docsUrl}/${relativeUrl}`;
            }
        }
        return null;
    }
    /** Create editor URL pointing to editable page. */
    editorUrl(item) {
        // return `https://cloud.supernova.io/ws/${this.workspaceHandle}/ds/${this.designSystemId}/${this.versionId}/documentation/editor/${item.id}`
        return `https://cloud.supernova.io/ws/${this.workspaceHandle}/ds/${this.designSystemId}-${this.slugify(this.designSystemName)}/${this.versionId}/documentation/editor/${item.id}`;
    }
    /** Create page URL or group URL pointing to the first contained page */
    pageUrl(object) {
        var _a;
        let page = null;
        if (object.type === DocumentationLegacyItemType.page) {
            page = object;
        }
        else {
            page = this.firstPageFromTop(object);
        }
        if (!page) {
            return "";
        }
        const pageSlug = (_a = page.userSlug) !== null && _a !== void 0 ? _a : page.slug;
        const subpaths = [];
        // Construct group path segments
        let { parent } = page;
        while (parent) {
            subpaths.push(this.slugify(parent.title));
            // @ts-expect-error TS(2322): Type 'DocumentationLegacyGroup | null' is not assi... Remove this comment to see the full error message
            parent = parent.parent;
        }
        // Remove last segment added, because we don't care about root group
        subpaths.pop();
        // Retrieve url-safe path constructed as [host][group-slugs][path-slug][.html]
        const path = `${[...subpaths.reverse(), pageSlug].join("/")}.html`;
        return path;
    }
    /** Find first showable page from the top of the provided root */
    firstPageFromTop(documentationRoot) {
        for (const child of documentationRoot.children) {
            if (child.type === DocumentationLegacyItemType.page) {
                return child;
            }
            const possiblePage = this.firstPageFromTop(child);
            if (possiblePage) {
                return possiblePage;
            }
        }
        return null;
    }
    /** Slugify text to be usable as URL */
    slugify(str) {
        if (!str) {
            return "";
        }
        let string = str;
        // Thanks to https://gist.github.com/codeguy/6684588
        string = str.replace(/^\s+|\s+$/g, "");
        string = str.toLowerCase();
        // remove accents, swap ñ for n, etc
        const from = "àáãäâèéëêìíïîòóöôùúüûñç·/_,:;";
        const to = "aaaaaeeeeiiiioooouuuunc------";
        for (let i = 0, l = from.length; i < l; i += 1) {
            string = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
        }
        string = str
            .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
            .replace(/\s+/g, "-") // collapse whitespace and replace by -
            .replace(/-+/g, "-"); // collapse dashes
        return string;
    }
}
